import { QuizShell } from './QuizShell';
import { OptionPill } from './OptionPill';
import { BookmakerPicker } from './BookmakerPicker';
import {
  SPORTS,
  BETTING_STYLES,
  STAKE_BANDS,
  FEATURES,
} from './quiz-data';
import type {
  Bookmaker,
  SportSlug,
  BettingStyleSlug,
  StakeBand,
  PlatformFeatureSlug,
  QuizAnswers,
} from '../types';

interface Q1Props {
  answers: QuizAnswers;
  onToggle: (s: SportSlug) => void;
  onNext: () => void;
}
export function Q1Sports({ answers, onToggle, onNext }: Q1Props) {
  const selected = answers.sports;
  return (
    <QuizShell
      step={1}
      totalSteps={5}
      answers={answers}
      heading="Which sports do you mainly bet on?"
      subtitle="Select all that apply — we'll filter out bookmakers with weak markets in your areas."
      onNext={onNext}
      nextDisabled={selected.length === 0}
      nextPrimary={selected.length > 0}
    >
      <div className="fb-quiz__options fb-quiz__options--grid">
        {SPORTS.map((s) => (
          <OptionPill
            key={s.slug}
            emoji={s.emoji}
            label={s.label}
            selected={selected.includes(s.slug)}
            onClick={() => onToggle(s.slug)}
          />
        ))}
      </div>
    </QuizShell>
  );
}

interface Q2Props {
  answers: QuizAnswers;
  onPick: (s: BettingStyleSlug) => void;
  onBack: () => void;
  onNext: () => void;
}
export function Q2BettingStyle({ answers, onPick, onBack, onNext }: Q2Props) {
  const selected = answers.bettingStyle;
  return (
    <QuizShell
      step={2}
      totalSteps={5}
      answers={answers}
      heading="What's your usual betting style?"
      subtitle="This helps us match you to the right offer types and bookmaker strengths."
      onBack={onBack}
      onNext={onNext}
      nextDisabled={!selected}
      nextPrimary={!!selected}
    >
      <div className="fb-quiz__options">
        {BETTING_STYLES.map((s) => (
          <OptionPill
            key={s.slug}
            label={s.label}
            selected={selected === s.slug}
            onClick={() => onPick(s.slug)}
          />
        ))}
      </div>
    </QuizShell>
  );
}

interface Q3Props {
  answers: QuizAnswers;
  onPick: (s: StakeBand) => void;
  onBack: () => void;
  onNext: () => void;
}
export function Q3StakeSize({ answers, onPick, onBack, onNext }: Q3Props) {
  const selected = answers.stakeBand;
  return (
    <QuizShell
      step={3}
      totalSteps={5}
      answers={answers}
      heading="What's your typical qualifying stake size?"
      subtitle="Some offers require £10 minimum, others only £5. We'll only show offers you can actually claim."
      onBack={onBack}
      onNext={onNext}
      nextDisabled={!selected}
      nextPrimary={!!selected}
    >
      <div className="fb-quiz__options">
        {STAKE_BANDS.map((s) => (
          <OptionPill
            key={s.slug}
            label={s.label}
            selected={selected === s.slug}
            onClick={() => onPick(s.slug)}
          />
        ))}
      </div>
    </QuizShell>
  );
}

interface Q4Props {
  answers: QuizAnswers;
  onToggle: (s: PlatformFeatureSlug) => void;
  onBack: () => void;
  onNext: () => void;
}
export function Q4Features({ answers, onToggle, onBack, onNext }: Q4Props) {
  const selected = answers.features;
  return (
    <QuizShell
      step={4}
      totalSteps={5}
      answers={answers}
      heading="Which platform features matter most to you?"
      subtitle="Pick your must-haves — we'll deprioritise bookmakers that don't offer them. Or skip if you're easy."
      onBack={onBack}
      onNext={onNext}
      nextDisabled={false}
      nextPrimary={true}
      nextLabel={selected.length === 0 ? 'Skip' : 'Continue'}
    >
      <div className="fb-quiz__options fb-quiz__options--grid">
        {FEATURES.map((f) => (
          <OptionPill
            key={f.slug}
            emoji={f.emoji}
            label={f.label}
            selected={selected.includes(f.slug)}
            onClick={() => onToggle(f.slug)}
          />
        ))}
      </div>
    </QuizShell>
  );
}

interface Q5Props {
  answers: QuizAnswers;
  bookmakers: Bookmaker[];
  onToggle: (id: number) => void;
  onBack: () => void;
  onNext: () => void;
}
export function Q5Bookmakers({ answers, bookmakers, onToggle, onBack, onNext }: Q5Props) {
  return (
    <QuizShell
      step={5}
      totalSteps={5}
      answers={answers}
      heading="Already registered anywhere?"
      subtitle="We'll exclude these from new-customer offers so you don't waste a click. Search or pick from the most popular below — totally optional."
      onBack={onBack}
      onNext={onNext}
      nextDisabled={false}
      nextLabel="🏁 Cross the line"
      nextPrimary={true}
    >
      <BookmakerPicker
        bookmakers={bookmakers}
        selected={answers.existingBookmakers}
        onToggle={onToggle}
      />
    </QuizShell>
  );
}
